import { msg, Trans } from '@lingui/macro'

import * as React from 'react'

import { useTrans } from '~logic/i18n/useTrans'
import { Button } from '~ui/components/buttons/Button/Button'
import { InputControl } from '~ui/components/formElements/accessories/InputControl'
import { InputLabel } from '~ui/components/formElements/accessories/InputLabel'
import { Input } from '~ui/components/formElements/Input/Input'
import { Box, Grid } from '~ui/components/layout/Box'
import { Skeleton } from '~ui/components/loadingSkeletons/Skeleton'
import { Text } from '~ui/components/typography/Text/Text'

import { useLayoutCtx } from '../../../config/contexts/LayoutProvider'
import {
  useProductDetailDataCtx,
  useProductDetailStateCtx,
  useUpdateProductDetailCtx,
} from '../contexts/ProductDetailStateProvider'

type TProps = NoChildren & {
  canCreateOrder?: boolean
}

const NOTE_LINES = 3
const NOTE_PLACEHOLDER = msg`Note related to the product that you are adding into the cart`

/**
 * Multiple-line input to add and edit the note about the product that is being added to the cart.
 * If there is no note `value` and `isInitiallyActive` prop is set to `false`,
 * only the opening button is rendered.
 */
export const ProductNote: React.FC<TProps> = ({ canCreateOrder = true }) => {
  const { trans } = useTrans()
  const { isMobile } = useLayoutCtx()

  const { product } = useProductDetailDataCtx()
  const { updateNote } = useUpdateProductDetailCtx()
  const { productNote, isLoading } = useProductDetailStateCtx()

  const isInitiallyActive = product.availableAddonCount > 0

  const [showInput, setShowInput] = React.useState(isInitiallyActive || Boolean(productNote))

  React.useEffect(() => {
    if (!Boolean(productNote)) {
      return
    }

    setShowInput(true)
  }, [productNote])

  if (!canCreateOrder) {
    return null
  }

  return (
    <Box
      alignSelf={`end`}
      padding={{
        top: `none`,
        bottom: `none`,
        left: isMobile ? `medium` : `none`,
        right: isMobile ? `medium` : `none`,
      }}
    >
      {showInput ? (
        <InputControl>
          <Grid rowGap={`small`}>
            <InputLabel fontStyle={`regular`} contrast={`low`} fontSize={`m18`} padding={`none`}>
              <Trans>Note</Trans>
            </InputLabel>
            <Input
              radius={`small`}
              value={productNote ?? ``}
              numberOfLines={NOTE_LINES}
              onChange={(note) => updateNote({ note })}
              placeholder={trans(NOTE_PLACEHOLDER)}
            />
          </Grid>
        </InputControl>
      ) : (
        <Skeleton showSkeleton={isLoading} maxWidth={`8rem`}>
          <Button
            variant={`ghost`}
            padding={`none`}
            shadow={`none`}
            onClick={() => setShowInput(true)}
          >
            <Text textDecoration={`underline`} contrast={`low`} lineHeight={1.5}>
              <Trans>add a note</Trans>
            </Text>
          </Button>
        </Skeleton>
      )}
    </Box>
  )
}
